import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { sendWhatsApp } from "@/lib/whatsapp";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PhoneCall, RotateCw, Share2, Crown, Loader2, MessageCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTerm } from "@/contexts/branding";

const RISK_TONE = {
  warning_30: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  critical_60: "bg-orange-500/10 text-orange-700 border-orange-500/30",
  lost_90: "bg-red-500/10 text-red-700 border-red-500/30",
};

function ClientRow({ row, kind, term }) {
  const c = row.client || {};
  let message = "";
  let badge = null;
  let extra = null;

  if (kind === "chamar_de_volta") {
    message = `Olá ${c.name}! Sentimos sua falta. Que tal agendar seu retorno de ${row.last_service_name || "atendimento"}? Já fazem ${row.days_since_last} dias da última visita.`;
    badge = <Badge variant="outline" className={RISK_TONE[row.risk_level]}>{row.days_since_last}d sem voltar</Badge>;
    extra = <span>Último: {row.last_service_name || "—"} · há {row.days_since_last} dias</span>;
  } else if (kind === "retornos_proximos") {
    message = `Oi ${c.name}! Está chegando a hora do seu retorno de ${row.last_service_name}. Posso reservar um horário pra você?`;
    badge = <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">em {row.due_in_days}d</Badge>;
    extra = <span>Último: {row.last_service_name} · próximo {new Date(row.next_return_at).toLocaleDateString("pt-BR")}</span>;
  } else if (kind === "pode_indicar") {
    message = `Olá ${c.name}! Você é cliente especial pra gente. Quer indicar alguém e ganhar pontos? Vou te enviar seu link de indicação.`;
    badge = <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/30">{c.points_balance || 0} pts</Badge>;
    extra = <span>{row.reason}</span>;
  } else if (kind === "merece_beneficio") {
    message = `Olá ${c.name}! Você acumulou ${c.points_balance} pontos. Que tal trocar por um benefício especial?`;
    badge = <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30"><Crown className="w-3 h-3 mr-1" />{c.points_balance} pts</Badge>;
    extra = <span>{row.reason}</span>;
  }

  const canSend = !!c.phone;

  return (
    <li className="px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-muted/30 transition-colors" data-testid={`recurrence-row-${kind}-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{c.name}</span>
          {badge}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{extra}</div>
        {c.phone && <div className="text-xs text-muted-foreground mt-0.5">📱 {c.phone}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canSend ? (
          <button
            type="button"
            onClick={() => sendWhatsApp({ phone: c.phone, text: message, clientId: c.id, context: `recurrence_${kind}` })}
            data-testid={`recurrence-wa-${kind}-${c.id}`}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md text-white"
            style={{ background: `hsl(var(--tenant-primary))` }}
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
        ) : (
          <span className="text-xs text-muted-foreground italic">Sem telefone</span>
        )}
      </div>
    </li>
  );
}

function BucketCard({ icon: Icon, title, subtitle, items, kind, term, testid }) {
  return (
    <Card className="rounded-tenant border-border" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border flex items-center gap-3">
        <div className="w-9 h-9 rounded-md grid place-items-center bg-muted">
          <Icon className="w-4 h-4" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <Badge variant="outline">{items.length}</Badge>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-muted-foreground">Nenhum cliente nesta lista agora. 🎉</div>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((r) => (
            <ClientRow key={r.client_id} row={r} kind={kind} term={term} />
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function TenantRecurrence() {
  const { slug } = useParams();
  const auth = useAuth();
  const term = useTerm(auth?.tenant);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/recurrence/overview");
      setData(data);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-10 text-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin inline" />
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Central de Recorrência</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            Cuide dos seus {term("client_plural", { lower: true })} antes de perdê-los
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Quem precisa ser chamado · quem volta em breve · quem pode indicar · quem merece um mimo
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { setRefreshing(true); load(); }}
          disabled={refreshing}
          data-testid="recurrence-refresh"
          className="gap-2"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <SummaryTile label="Chamar de volta" value={s.chamar_de_volta || 0} tone="bg-red-500/10 text-red-700" testid="summary-chamar" />
        <SummaryTile label="Retornos esta semana" value={s.retornos_proximos || 0} tone="bg-emerald-500/10 text-emerald-700" testid="summary-retornos" />
        <SummaryTile label="Pode indicar" value={s.pode_indicar || 0} tone="bg-blue-500/10 text-blue-700" testid="summary-indicar" />
        <SummaryTile label="Merece benefício" value={s.merece_beneficio || 0} tone="bg-amber-500/10 text-amber-700" testid="summary-beneficio" />
      </div>

      <Tabs defaultValue="chamar">
        <TabsList>
          <TabsTrigger value="chamar" data-testid="tab-chamar">Chamar de volta</TabsTrigger>
          <TabsTrigger value="retornos" data-testid="tab-retornos">Retornos esta semana</TabsTrigger>
          <TabsTrigger value="indicar" data-testid="tab-indicar">Pode indicar</TabsTrigger>
          <TabsTrigger value="beneficio" data-testid="tab-beneficio">Merece benefício</TabsTrigger>
        </TabsList>
        <TabsContent value="chamar" className="mt-5">
          <BucketCard
            icon={PhoneCall}
            title="Clientes para reativar"
            subtitle="Passaram do prazo de retorno sugerido. Quanto mais tempo, mais urgente."
            items={data.chamar_de_volta}
            kind="chamar_de_volta"
            term={term}
            testid="bucket-chamar"
          />
        </TabsContent>
        <TabsContent value="retornos" className="mt-5">
          <BucketCard
            icon={RotateCw}
            title="Próximos 7 dias"
            subtitle="Retornos sugeridos por serviço — ofereça antes que esqueçam."
            items={data.retornos_proximos}
            kind="retornos_proximos"
            term={term}
            testid="bucket-retornos"
          />
        </TabsContent>
        <TabsContent value="indicar" className="mt-5">
          <BucketCard
            icon={Share2}
            title="Indicadores em potencial"
            subtitle="Clientes engajados (100+ pts) que ainda não fizeram nenhuma indicação."
            items={data.pode_indicar}
            kind="pode_indicar"
            term={term}
            testid="bucket-indicar"
          />
        </TabsContent>
        <TabsContent value="beneficio" className="mt-5">
          <BucketCard
            icon={Crown}
            title="Top fiéis"
            subtitle="Acumularam 500+ pontos. Vale a pena oferecer um benefício para reter."
            items={data.merece_beneficio}
            kind="merece_beneficio"
            term={term}
            testid="bucket-beneficio"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  return (
    <Card className="p-4 rounded-tenant border-border" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-3xl font-bold">{value}</div>
        <div className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tone}`}>cliente{value !== 1 ? "s" : ""}</div>
      </div>
    </Card>
  );
}
